import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Component, ChangeDetectionStrategy, inject, signal, computed, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { VersionInfoService, CombinedVersionInfo } from '@services/version-info.service';

@Component({
  selector: 'app-version-info',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    TooltipModule,
    ProgressSpinnerModule
  ],
  templateUrl: './version-info.component.html',
  styleUrls: ['./version-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VersionInfoComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  private versionInfoService = inject(VersionInfoService);
  
  isOpen = signal(false);
  combinedInfo = signal<CombinedVersionInfo | null>(null);
  loading = signal(false);
  error = signal<string | null>(null);
  private dataLoaded = false;
  
  // Computed for frontend
  frontendVersion = computed(() => this.combinedInfo()?.frontend);
  frontendFormattedSha = computed(() => {
    const info = this.frontendVersion();
    if (!info?.commitSha) return 'N/A';
    return this.versionInfoService.formatSha(info.commitSha);
  });
  frontendFormattedDeployDate = computed(() => {
    const info = this.frontendVersion();
    if (!info?.deployDateTime) return 'N/A';
    return this.versionInfoService.formatDeployDate(info.deployDateTime);
  });
  frontendRelativeTime = computed(() => {
    const info = this.frontendVersion();
    if (!info?.deployDateTime) return '';
    return this.versionInfoService.getRelativeTime(info.deployDateTime);
  });
  
  // Computed for backend
  backendVersion = computed(() => this.combinedInfo()?.backend);
  backendFormattedSha = computed(() => {
    const info = this.backendVersion();
    if (!info?.commitSha) return 'N/A';
    return this.versionInfoService.formatSha(info.commitSha);
  });
  backendFormattedDeployDate = computed(() => {
    const info = this.backendVersion();
    if (!info?.deployDateTime) return 'N/A';
    return this.versionInfoService.formatDeployDate(info.deployDateTime);
  });
  backendRelativeTime = computed(() => {
    const info = this.backendVersion();
    if (!info?.deployDateTime) return '';
    return this.versionInfoService.getRelativeTime(info.deployDateTime);
  });
  
  hasBackend = computed(() => this.backendVersion() !== null);
  
  ngOnInit(): void {
    this.loadVersionInfo();
  }
  
  open(): void {
    if (!this.dataLoaded) {
      this.loadVersionInfo();
    }
    this.isOpen.set(true);
    document.body.style.overflow = 'hidden';
  }
  
  close(): void {
    this.isOpen.set(false);
    document.body.style.overflow = '';
  }
  
  private loadVersionInfo(): void {
    if (this.loading()) return;
    
    this.loading.set(true);
    this.error.set(null);
    
    this.versionInfoService.getCombinedVersionInfo().pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.combinedInfo.set(data);
        this.loading.set(false);
        this.dataLoaded = true;
      },
      error: (err) => {
        console.error('❌ Error loading version info:', err);
        this.error.set('Failed to load version information');
        this.loading.set(false);
      }
    });
  }
  
  async copyFrontendShaToClipboard(): Promise<void> {
    const sha = this.frontendVersion()?.commitSha;
    if (!sha || sha === 'N/A' || sha === 'development') return;
    try {
      await navigator.clipboard.writeText(sha);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }
  
  async copyBackendShaToClipboard(): Promise<void> {
    const sha = this.backendVersion()?.commitSha;
    if (!sha || sha === 'N/A') return;
    try {
      await navigator.clipboard.writeText(sha);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }
  
  refresh(): void {
    this.dataLoaded = false;
    this.combinedInfo.set(null);
    this.error.set(null);
    this.loadVersionInfo();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}